<template>
  <div>
    <api-errors :errors="errors" />
    <left-side-panel-header
      compact-mode-breakpoint="lg"
      :title="`Assign Tables${$vuetify.breakpoint.lgAndUp ? ' to Server' : ''}`"
      :is-dirty="isDirty"
      @click:back="clickBack"
      @click:save="savePageData"
    >
      <template #lock-timer>
        <lock-timer
          v-bind="getLockTimerSettings(
            [itemData], 
            {
              children: true, 
              recordType: 'diningServer',
              include_fields: ['diningServerTables.locationId', 'diningServerTables.locationName'],
            },
            () => $emit('deselect-server')
          )"
        />
      </template>
    </left-side-panel-header>
    <v-divider />
    <v-row 
      no-gutters
      class="d-flex justify-center"
    >
      <div class="d-flex py-5">
        <server-initial-icon 
          :server="server"
          large
          class="pr-2"
        />
        <div class="title d-flex align-self-center">
          {{ server.name }}
        </div>
      </div>
    </v-row>
    <loading-bar v-if="!!awaitingApiResponse" />
    <div v-else>
      <v-row 
        v-if="$vuetify.breakpoint.mdAndUp"
        no-gutters
        class="px-4"
      >
        <v-col cols="5">
          Table #
        </v-col>
        <v-col cols="5">
          Covers
        </v-col>
      </v-row>
      <div 
        v-else
        class="pl-2"
      >
        Assigned Tables
      </div>
      <div v-if="!(itemData.tables || []).length">
        <v-card class="ma-2 pa-2 font-weight-bold text-center">
          Currently Not Assigned to a Table
        </v-card>
      </div>
      <div v-else>
        <v-fade-transition group>
          <table-card 
            v-for="table in itemData.tables"
            :key="'server-table-' + table.locationTableId"
            :table="allTablesMap[table.locationTableId]"
            allow-remove
            :location="table.locationId !== locationId ? table.locationName : ''"
            @click:remove="removeTable(allTablesMap[table.locationTableId])"
          >
            <v-icon color="error">
              mdi-trash-can-outline
            </v-icon>
          </table-card>
        </v-fade-transition>
      </div>
      <v-row 
        no-gutters
        class="justify-space-between"
      >
        <div class="font-weight-bold pa-2">
          {{ `Total Tables: ${(itemData.tables || []).length}/${server.maxTables}` }}
        </div>
        <div class="font-weight-bold pa-2">
          {{ `Total Covers: ${totalCovers}/${server.maxCovers}` }}
        </div>
      </v-row>
      <simple-expansion-card value>
        <template #title>
          Available Tables
        </template>
        <template #outer-content>
          <v-card 
            v-if="!tables.length"
            class="ma-2 pa-2 d-flex justify-center font-weight-bold"
          >
            No Available Tables...
          </v-card>
          <v-row 
            v-else-if="$vuetify.breakpoint.mdAndUp"
            no-gutters
            class="px-4"
          >
            <v-col cols="5">
              Table #
            </v-col>
            <v-col cols="5">
              Covers
            </v-col>
          </v-row>
        </template>
        <table-card 
          v-for="table in tables"
          :key="'unassigned-table-' + table.id"
          :table="table"
          :servers="serverAssignmentsMap[table.id]"
          :allow-add="!selectedTablesMap[table.id]"
          :allow-remove="!!selectedTablesMap[table.id]"
          :selected="!!selectedTablesMap[table.id]"
          @click:add="addTable(table)"
          @click:remove="removeTable(table)"
        >
          <v-icon color="primary">
            mdi-plus
          </v-icon>
        </table-card>
      </simple-expansion-card>
    </div>
  </div>
</template>

<script>
import recordLockHelper from '@/ft-vue-core/mixins/recordLockHelper'
import floorPlanHelpers from '../mixins/floorPlanHelpers'
import SimpleExpansionCard from '../helperComponents/SimpleExpansionCard'
import TableCard from '../helperComponents/TableCard'
import ServerInitialIcon from '../helperComponents/ServerInitialIcon'
export default {
  name: 'SelectedServer',
  components: {SimpleExpansionCard, TableCard, ServerInitialIcon},
  mixins: [floorPlanHelpers, recordLockHelper],
  props: {
    server: {
      type: Object,
      default () {return {}}
    },
    date: [String, Number],
    locationId: Number,
    mealPeriodId: Number,
    tableConfigId: Number,
    multiSelectMode: Boolean,
    serverAssignmentsMap: Object,
  },
  data () {
    return {
      apiRecordCommand: 'diningServer',
      awaitingApiResponse: 0,
      tables: [],
      allTables: [],
      itemData: {},
      isDirty: false,
      tableResourcesLoaded: 0,
    }
  },
  computed: {
    selectedTables () {
      return this.itemData.tables ?? []
    },
    totalCovers () {
      return this.selectedTables?.reduce((acc, r) => {
        acc += r.tableMaxCovers ?? 0
        return acc
      }, 0) ?? 0
    },
    selectedTablesMap () {
      return this.selectedTables?.reduce((acc, r) => {
        acc[r.locationTableId] = r
        return acc
      }, {}) ?? {}
    },
    tablesMap () {
      return this.tables?.reduce((acc, r) => {
        acc[r.id] = r
        return acc
      }, {}) ?? {}
    },
    allTablesMap () {
      return this.allTables?.reduce((acc, r) => {
        acc[r.id] = r
        return acc
      }, {}) ?? {}
    },
    unassignedTables () {
      return this.tables.filter(t => !this.selectedTablesMap[t.id])
    }
  },
  watch: {
    tableResourcesLoaded (value) {
      if (value !== 2) return
      this.$emit('add-tables', true, this.itemData.tables?.map(t => this.tablesMap[t.locationTableId]))
    }
  },
  mounted () {
    const commands = [
      this.withResourceLockHandler({
        command: this.apiRecordCommand,
        loading: this.isLoading,
        afterLoad: (r) => {
          this.tableResourcesLoaded += 1
          this.awaitingApiResponse -= 1
          this.itemData = r?.[0] ?? {}
          this.isDirty = false
        },  
        params: {
          children: true,
          filter_methods: {filterTablesByDate: [this.date]},
          include_fields: ['diningServerTables.locationId', 'diningServerTables.locationName'],
          filter: [
            {field: 'id', operator: 'EQUAL', values: [this.server.serverId]},
            {field: 'diningServerTables.mealPeriodId', operator: 'EQUAL', values: [this.mealPeriodId]},
          ]
        }
      }),
      {
        command: 'diningLocationTable',
        load: this.tables,
        afterLoad: () => {
          this.tableResourcesLoaded += 1
          this.awaitingApiResponse -= 1
        },
        params: {
          filter: [
            {field: 'locationId', operator: 'EQUAL', values: [this.locationId]},
            {field: 'disabled', operator: 'EQUAL', values: [false]}
          ],
          filter_methods: {
            filterByShowOnFloorPlan: [true, this.tableConfigId],
            filterByOnTableConfig: [this.tableConfigId]
          }
        },
      },
      {
        command: 'diningLocationTable',
        load: this.allTables,
        afterLoad: () => this.awaitingApiResponse -= 1,
        loading: this.isLoading,
      }
    ]
    this.awaitingApiResponse = commands.length
    this.$ftAPI().get(this.addResourceErrorHandler(commands))
  },
  beforeDestroy () {
    this.$emit('add-tables', false)
    this.$ftAPI().get(this.addResourceErrorHandler({
      command: 'releaseLock',
      params: {record_key: this.itemData.recordLockKey}
    }))
  },
  methods: {
    clickBack (isDirty, confirmed) {
      if (isDirty && !confirmed) {
        this.$vPrompt({
          title: 'Unsaved Changes',
          titleIcon: 'mdi-alert-circle',
          titleIconColor: 'warning',
          text: 'Discard unsaved changes to this Server?',
          confirmText: 'Discard',
          confirmAction: () => this.clickBack(isDirty, true),
          cancelAction: () => {}
        })
      } else this.$emit('deselect-server')
    },
    removeTable (table) {
      const spliceIndex = this.itemData.tables.findIndex(t => t.locationTableId === table.id)
      if (spliceIndex !== -1) {
        this.itemData.tables.splice(spliceIndex, 1)
        this.$emit('add-table', this.itemData.tables?.map(t => this.allTablesMap[t.locationTableId]))
      }
    },
    setTables (tables = []) {
      this.itemData.tables = tables
    },
    addTable (table) {
      if (!this.itemData.tables) {
        this.$set(this.itemData, 'tables', [])
      }
      this.itemData.tables.push({
        locationTableId: table.id, 
        tableMaxCovers: table.maxCovers,
        tableName: table.name,
      })
      if (this.multiSelectMode) {
        this.$emit('add-table', this.itemData.tables?.map(t => this.allTablesMap[t.locationTableId]))
      }
    },
    savePageData () {
      const command = {
        command: 'diningSaveServerTablesByDate',
        loading: this.isLoading,
        afterLoad: () => {
          this.$emit('deselect-server')
        },
        params: {
          children: true
        }
      }
      this.awaitingApiResponse = 1
      this.$ftAPI().put(this.addResourceErrorHandler(command), {
        server: this.itemData,
        date: this.date,
        mealPeriodId: this.mealPeriodId,
        tables: this.itemData.tables.map(t => {
          return Object.assign(
            t, {date: this.date, mealPeriodId: this.mealPeriodId, serverId: this.itemData.id}
          )
        })
      })
    }
  }
}
</script>